import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar, Rocket, CheckCircle2 } from "lucide-react";
import { Analytics } from "@/lib/analytics";
import { signupUrl } from "@/lib/productApp";
import smartphoneMockup from "@/assets/marketing/immovox-smartphone-mockup.png";
import CalendlyModal from "@/components/CalendlyModal";
import { AnimatePresence, motion } from "framer-motion";
import type { AdsMode } from "./AdsModeSelector";

const heroContent = {
  vente: {
    titleLine1: "Un appel suffit",
    titleLine2: "à perdre un mandat.",
    subtitle:
      "Vous êtes en rendez-vous. Le vendeur appelle. Personne ne décroche. Il signe ailleurs.",
  },
  location: {
    titleLine1: "Une annonce publiée,",
    titleLine2: "35 appels qui tombent.",
    subtitle:
      "En moyenne 35 appels par annonce. Combien valent vraiment le coup ?",
  },
} as const;

const AdsHero = ({ mode, onModeChange }: { mode: AdsMode; onModeChange: (m: AdsMode) => void }) => {
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  const content = heroContent[mode];

  return (
    <>
      <section className="relative min-h-[80vh] flex items-center pt-20 overflow-hidden bg-gradient-warm">
        <div className="absolute top-20 -left-32 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute bottom-10 right-0 w-80 h-80 rounded-full bg-accent/5 blur-3xl" />

        <div className="container mx-auto px-4 py-16 lg:py-24 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="lg:w-[55%] text-center lg:text-left">
              {/* Mode toggle pill */}
              <p className="text-sm font-medium text-foreground/70 mb-2">Vous faites de la…</p>
              <motion.div
                initial={{ scale: 0.95, boxShadow: "0 0 0 0 hsl(var(--primary) / 0.4)" }}
                animate={{ scale: [0.95, 1.05, 1], boxShadow: ["0 0 0 0 hsl(var(--primary) / 0.4)", "0 0 0 8px hsl(var(--primary) / 0)", "0 0 0 0 hsl(var(--primary) / 0)"] }}
                transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
                className="inline-flex items-center rounded-full border border-border bg-muted/50 p-1.5 mb-8 shadow-sm"
              >
                <button
                  onClick={() => onModeChange("vente")}
                  className={`px-5 py-2 rounded-full text-sm md:text-base font-medium transition-all ${mode === "vente" ? "bg-primary text-primary-foreground shadow-sm" : "text-foreground/60 hover:text-foreground"}`}
                >
                  Transaction
                </button>
                <button
                  onClick={() => onModeChange("location")}
                  className={`px-5 py-2 rounded-full text-sm md:text-base font-medium transition-all ${mode === "location" ? "bg-primary text-primary-foreground shadow-sm" : "text-foreground/60 hover:text-foreground"}`}
                >
                  Gestion locative
                </button>
              </motion.div>

              {/* Animated stat + dynamic copy block */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={mode}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                >
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-6">
                    {content.titleLine1}
                    <br />
                    <span className="text-primary">{content.titleLine2}</span>
                  </h1>

                  <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl px-2 md:px-0">
                    {content.subtitle}
                  </p>
                </motion.div>
              </AnimatePresence>

              {/* Static CTA + reassurance */}
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Button
                  size="lg"
                  className="h-14 md:h-16 px-6 md:px-10 bg-primary hover:bg-primary/90 text-primary-foreground text-base md:text-lg font-bold rounded-full shadow-warm-lg hover:shadow-warm transition-all group max-w-full"
                  asChild
                >
                  <a
                    href={signupUrl()}
                    onClick={() => Analytics.ctaClicked("essai_gratuit", "ads_hero")}
                  >
                    <Rocket className="w-5 h-5 mr-2" />
                    Essai gratuit 7 jours
                  </a>
                </Button>

                <Button
                  size="lg"
                  variant="ghost"
                  className="h-14 px-6 text-muted-foreground hover:text-foreground rounded-full group"
                  onClick={() => {
                    Analytics.ctaClicked("planifier_demo", "ads_hero");
                    setCalendlyOpen(true);
                  }}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Réserver une démo
                </Button>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-2 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-primary" />
                  Actif en 30 minutes
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-primary" />
                  Votre numéro ne change pas
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-primary" />
                  Sans engagement
                </span>
              </div>
            </div>

            <div className="lg:w-[45%] flex justify-center">
              <div className="relative w-full max-w-sm">
                <div className="rounded-3xl overflow-hidden" style={{ maskImage: 'radial-gradient(ellipse at center, black 70%, transparent 100%)', WebkitMaskImage: 'radial-gradient(ellipse at center, black 70%, transparent 100%)' }}>
                  <img
                    src={smartphoneMockup}
                    alt="Application Immovox – notification d'appels manqués et fiche client sur smartphone"
                    className="w-full animate-float drop-shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <CalendlyModal open={calendlyOpen} onOpenChange={setCalendlyOpen} source="ads_hero" />
    </>
  );
};

export default AdsHero;
